import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FlexProps, Spinner } from '@chakra-ui/react'
import { Flex, Box, Center } from '@chakra-ui/react'
import { configureAbly, useChannel } from '@ably-labs/react-hooks'
import { gql, useApolloClient, useQuery, NetworkStatus } from '@apollo/client'

import {
    MessageType,
    ChatFieldsFragment,
    ChatMessageFieldsFragment,
    ClientPhoneFieldsFragment,
    GetChatMessagesByChatIdDocument,
    GetChatMessagesByChatIdQuery,
    GetChatMessagesByChatIdQueryVariables,
    useGetChatMessagesByChatIdQuery,
    useGetChatsByClientIdQuery,
    useUpdateChatMutation,
} from '~graphql/generated/graphql'
import ChatViewInput from './input'
import { useChat } from './chat-context'
import { wasChatSentFromThisTabRecently } from './input/send-message-lock'
import ChatViewHeader from './chat-view-header'
import ChatClientInfo from '../chat-client-info'
import MessagesList from './messages-list/messages-list'
import ExportMessagesProvider from './export-messages-context'
import { scrollbarCSS } from '~components/dashboard/action-item-list'
import { constructChannel, ChannelTypeNames } from '~utils/ably-helpers'
import { isSamePhoneNumber } from '~utils/phone'
import FetchMoreWrapper from '~components/shared/wrappers/fetch-more-wrapper'
import { useUnreadMessages } from '~contexts/unread-messages-provider'
import { useDisclosure } from '@chakra-ui/react'
import ChatClientInfoDrawer from '../chat-client-info/info-drawer'
import { LocalStorageKeys } from '~config/constants'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import SendToUpdateAlert from './send-to-update-alert'

interface Props {
    chat: ChatFieldsFragment
    onBack: () => void
    /**
     * Optional overrides for the root container's responsive size.
     * Used by embedded contexts like the Whiteboard chat popover where
     * the inbox-style breakpoint widths don't apply.
     */
    containerProps?: FlexProps
    /**
     * If true, suppresses the desktop client-info side panel and drawer.
     * Useful when ChatView is embedded in a small fixed-size container.
     */
    hideClientInfo?: boolean
    /**
     * Compact layout for embedded contexts like the Whiteboard chat popover.
     * Propagates to the header (denser layout, fewer toolbar buttons) and
     * the input (hides secondary action buttons).
     */
    compact?: boolean
    /**
     * In compact mode, renders a close (X) button in the header that calls
     * this callback. Used by the chat popover to close itself.
     */
    onCloseWindow?: () => void
    /**
     * In compact mode, renders a minimize button in the header that calls
     * this callback. Used by the chat popover to collapse to a title bar.
     */
    onMinimize?: () => void
    /** Pre-fills the input draft (only when no existing draft exists). */
    initialDraft?: string
    /** Auto-fires `suggestChatResponse` with this prompt and seeds the input. */
    aiPrompt?: string
}

const PAGE_SIZE = 25

type ChatWithMaybePmsDb = ChatFieldsFragment & {
    clinic: ChatFieldsFragment['clinic'] & {
        pms_db?: { id: string } | null
    }
}

type ChatClinicPmsDbQuery = {
    clinic: {
        id: string
        pms_db?: { id: string } | null
    }
}

const CHAT_CLINIC_PMS_DB_QUERY = gql`
    query chatClinicPmsDb($id: ID!) {
        clinic(id: $id) {
            id
            pms_db {
                id
            }
        }
    }
`

export default function ChatView({
    chat,
    onBack,
    containerProps,
    hideClientInfo,
    compact,
    onCloseWindow,
    onMinimize,
    initialDraft,
    aiPrompt,
}: Props) {
    const apolloClient = useApolloClient()
    const { refreshUnreadMessages } = useUnreadMessages()
    const {
        debouncedPatientFilter,
        debouncedShowSystem,
        availablePhones,
        setClientPhone,
        defaultPhone,
    } = useChat()

    const [showClientInfo, setShowClientInfo] = useState<boolean>(true)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const {
        isOpen: isSendToUpdateAlertOpen,
        onOpen: onOpenSendToUpdateAlert,
        onClose: onCloseSendToUpdateAlert,
    } = useDisclosure()
    const [pendingClientPhone, setPendingClientPhone] = useState<
        ClientPhoneFieldsFragment | undefined
    >()
    const hasResolvedClientPhoneRef = useRef(false)
    const [showMessagesForAllLocations, setShowMessagesForAllLocations] =
        useLocalStorage<boolean>(
            LocalStorageKeys.chatsShowMessagesForAllLocations,
            false,
        )
    const [allLocationMessages, setAllLocationMessages] = useState<
        ChatMessageFieldsFragment[]
    >([])
    const [allLocationMessagesLoading, setAllLocationMessagesLoading] =
        useState(false)

    const page = useRef<number>(0)
    const hasReachedEnd = useRef<boolean>(false)

    const { data: clinicPmsDbData } = useQuery<ChatClinicPmsDbQuery>(
        CHAT_CLINIC_PMS_DB_QUERY,
        {
            variables: { id: chat.clinic.id },
            skip: !chat.clinic.id,
        },
    )
    const chatClinicPmsDb = (chat as ChatWithMaybePmsDb).clinic.pms_db
    const canShowMessagesForAllLocations = Boolean(
        chatClinicPmsDb || clinicPmsDbData?.clinic?.pms_db,
    )
    const shouldShowMessagesForAllLocations =
        canShowMessagesForAllLocations && showMessagesForAllLocations

    const messagesQueryVariables =
        useMemo<GetChatMessagesByChatIdQueryVariables>(
            () => ({
                chatId: chat.id,
                page: 0,
                pageSize: PAGE_SIZE,
                patientIds:
                    debouncedPatientFilter.length > 0
                        ? debouncedPatientFilter
                        : undefined,
                showSystemMessages: debouncedShowSystem,
            }),
            [chat.id, debouncedPatientFilter, debouncedShowSystem],
        )

    const { data: allLocationChatsData, loading: allLocationChatsLoading } =
        useGetChatsByClientIdQuery({
            variables: {
                clientId: chat.client.id,
            },
            skip: !shouldShowMessagesForAllLocations,
        })
    const allLocationChatIds = React.useMemo(() => {
        if (!allLocationChatsData?.getChatsByClientId) return []

        const chatIds = allLocationChatsData.getChatsByClientId.map(
            chat => chat.id,
        )

        return chatIds.includes(chat.id) ? chatIds : [chat.id, ...chatIds]
    }, [allLocationChatsData?.getChatsByClientId, chat.id])

    const {
        data: chatMessagesData,
        previousData: previousChatMessagesData,
        networkStatus: chatMessagesNetworkStatus,
        refetch: refetchChatMessages,
        fetchMore: fetchMoreChatMessages,
    } = useGetChatMessagesByChatIdQuery({
        variables: messagesQueryVariables,
        onCompleted: data => {
            if (hasResolvedClientPhoneRef.current) return
            if (page.current !== 0) return
            if (!data?.getChatMessagesByChatId.length) return

            hasResolvedClientPhoneRef.current = true

            const messageWithClientPhone = data.getChatMessagesByChatId
                .filter(m => m.message_type !== MessageType.System)
                .find(m => m.client_phone)

            const messagePhone = messageWithClientPhone?.client_phone?.phone
            const clinicCountry = chat.clinic?.address?.country

            const matchingCurrentClientPhone = availablePhones.find(
                p =>
                    p.phone === messagePhone ||
                    isSamePhoneNumber(p.phone, messagePhone, clinicCountry),
            )

            if (!matchingCurrentClientPhone) return

            if (matchingCurrentClientPhone.id === defaultPhone?.id) {
                setClientPhone(matchingCurrentClientPhone)
                return
            }

            // Same number stored twice (e.g. 10-digit vs 11-digit duplicates
            // left by the storage-format migration): follow the primary
            // silently instead of popping an alert that lists two identical
            // numbers.
            if (
                defaultPhone &&
                isSamePhoneNumber(
                    matchingCurrentClientPhone.phone,
                    defaultPhone.phone,
                    clinicCountry,
                )
            ) {
                setClientPhone(defaultPhone)
                return
            }

            setPendingClientPhone(matchingCurrentClientPhone)
            onOpenSendToUpdateAlert()
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
        skip: shouldShowMessagesForAllLocations,
    })

    const fetchAllLocationMessages = useCallback(async () => {
        if (!shouldShowMessagesForAllLocations || !allLocationChatIds.length) {
            setAllLocationMessages([])
            return
        }

        setAllLocationMessagesLoading(true)

        try {
            const messageGroups = await Promise.all(
                allLocationChatIds.map(chatId =>
                    fetchAllMessagesForChat({
                        apolloClient,
                        chatId,
                        patientIds: debouncedPatientFilter,
                        showSystemMessages: debouncedShowSystem,
                    }),
                ),
            )

            setAllLocationMessages(dedupeAndSortMessages(messageGroups.flat()))
        } finally {
            setAllLocationMessagesLoading(false)
        }
    }, [
        allLocationChatIds,
        apolloClient,
        debouncedPatientFilter,
        debouncedShowSystem,
        shouldShowMessagesForAllLocations,
    ])

    useEffect(() => {
        if (!shouldShowMessagesForAllLocations) return
        if (allLocationChatsLoading) return

        fetchAllLocationMessages()
    }, [
        allLocationChatsLoading,
        fetchAllLocationMessages,
        shouldShowMessagesForAllLocations,
    ])

    useEffect(() => {
        if (shouldShowMessagesForAllLocations) return

        setAllLocationMessages([])
    }, [shouldShowMessagesForAllLocations])

    useEffect(() => {
        if (allLocationChatsLoading) return
        if (allLocationChatIds.length) return
        if (!shouldShowMessagesForAllLocations) return

        setAllLocationMessagesLoading(true)
        fetchAllMessagesForChat({
            apolloClient,
            chatId: chat.id,
            patientIds: debouncedPatientFilter,
            showSystemMessages: debouncedShowSystem,
        })
            .then(messages => {
                setAllLocationMessages(dedupeAndSortMessages(messages))
            })
            .finally(() => {
                setAllLocationMessagesLoading(false)
            })
    }, [
        allLocationChatIds.length,
        allLocationChatsLoading,
        apolloClient,
        chat.id,
        debouncedPatientFilter,
        debouncedShowSystem,
        shouldShowMessagesForAllLocations,
    ])

    const allLocationMessagesNetworkStatus = allLocationMessagesLoading ? 1 : 7

    async function fetchAllMessagesForChat({
        apolloClient,
        chatId,
        patientIds,
        showSystemMessages,
        page = 0,
        accumulatedMessages = [],
    }: {
        apolloClient: ReturnType<typeof useApolloClient>
        chatId: string
        patientIds: string[]
        showSystemMessages: boolean
        page?: number
        accumulatedMessages?: ChatMessageFieldsFragment[]
    }): Promise<ChatMessageFieldsFragment[]> {
        const result = await apolloClient.query<
            GetChatMessagesByChatIdQuery,
            GetChatMessagesByChatIdQueryVariables
        >({
            query: GetChatMessagesByChatIdDocument,
            variables: {
                chatId,
                page,
                pageSize: PAGE_SIZE,
                patientIds: patientIds.length ? patientIds : undefined,
                showSystemMessages,
            },
            fetchPolicy: 'network-only',
        })

        const pageMessages = result.data.getChatMessagesByChatId
        const nextMessages = [...accumulatedMessages, ...pageMessages]

        if (pageMessages.length < PAGE_SIZE) return nextMessages

        return fetchAllMessagesForChat({
            apolloClient,
            chatId,
            patientIds,
            showSystemMessages,
            page: page + 1,
            accumulatedMessages: nextMessages,
        })
    }

    function dedupeAndSortMessages(messages: ChatMessageFieldsFragment[]) {
        return Array.from(
            messages
                .reduce((messagesById, message) => {
                    messagesById.set(message.id, message)
                    return messagesById
                }, new Map<string, ChatMessageFieldsFragment>())
                .values(),
        ).sort((a, b) => b.created_at - a.created_at)
    }

    useEffect(() => {
        page.current = 0
        hasReachedEnd.current = false
    }, [
        chat.id,
        debouncedPatientFilter,
        debouncedShowSystem,
        shouldShowMessagesForAllLocations,
    ])

    useEffect(() => {
        hasResolvedClientPhoneRef.current = false
        setPendingClientPhone(undefined)
        onCloseSendToUpdateAlert()
    }, [chat.id])

    const messages =
        (shouldShowMessagesForAllLocations
            ? allLocationMessages
            : chatMessagesData?.getChatMessagesByChatId ||
              previousChatMessagesData?.getChatMessagesByChatId) || []
    const networkStatus = shouldShowMessagesForAllLocations
        ? allLocationMessagesNetworkStatus
        : chatMessagesNetworkStatus
    const isLoadingMessages =
        shouldShowMessagesForAllLocations &&
        (allLocationChatsLoading || allLocationMessagesLoading)
    const showMessagesSpinner =
        (networkStatus === NetworkStatus.loading && messages.length === 0) ||
        (isLoadingMessages && messages.length === 0)
    const hasMore =
        !shouldShowMessagesForAllLocations &&
        messages.length === page.current * PAGE_SIZE + PAGE_SIZE &&
        !hasReachedEnd.current

    async function handleFetchMore() {
        if (shouldShowMessagesForAllLocations) return

        try {
            await fetchMoreChatMessages({
                variables: {
                    chatId: chat.id,
                    page: page.current + 1,
                    pageSize: PAGE_SIZE,
                    patientIds:
                        debouncedPatientFilter.length > 0
                            ? debouncedPatientFilter
                            : undefined,
                    showSystemMessages: debouncedShowSystem,
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) {
                        return prev
                    }
                    return {
                        getChatMessagesByChatId: [
                            ...prev.getChatMessagesByChatId,
                            ...fetchMoreResult.getChatMessagesByChatId,
                        ],
                    }
                },
            })
            page.current = page.current + 1
        } catch (error) {
            console.error('error', error)
            hasReachedEnd.current = true
        }
    }

    configureAbly({
        authUrl: `${process.env.GATSBY_REST_API_URL}/ably/auth`,
    })

    useChannel(
        constructChannel(ChannelTypeNames.newMessage, chat?.id || ''),
        () => {
            if (wasChatSentFromThisTabRecently(chat.id)) {
                return
            }

            if (shouldShowMessagesForAllLocations) {
                fetchAllLocationMessages()
                return
            }

            refetchChatMessages()
        },
    )

    const unreadCountRef = useRef(0)

    useEffect(() => {
        unreadCountRef.current = chat.clinic_unread_count
    }, [chat.clinic_unread_count])

    const [updateChat] = useUpdateChatMutation({
        onCompleted: () => {
            refreshUnreadMessages()
        },
    })

    const handleUpdateChat = (unreadCount: number = 0) => {
        updateChat({
            variables: {
                id: chat.id,
                data: {
                    clinic_last_read: Date.now(),
                    clinic_last_read_count: chat.client_message_count,
                    clinic_unread_count: unreadCount > 0 ? 1 : 0,
                },
            },
        })
    }

    useEffect(() => {
        handleUpdateChat()
        return () => {
            handleUpdateChat(unreadCountRef.current)
        }
    }, [])

    return (
        <>
            <Flex
                flex={1}
                h="full"
                w={{ base: '100vw', lg: 'auto' }}
                bg="white"
                minW={{ base: 'none', lg: '360px' }}
                rounded={{ base: 0, lg: '2xl' }}
                borderWidth={{ base: 0, lg: 1 }}
                flexDir="column"
                overflow="hidden"
                shadow="blue.lg"
                position="relative"
                borderColor="gray.200"
                {...containerProps}
            >
                <ExportMessagesProvider messages={messages}>
                    <ChatViewHeader
                        onBack={onBack}
                        showClientInfo={showClientInfo}
                        onOpen={() => setShowClientInfo(true)}
                        onOpenDrawer={onOpen}
                        compact={compact}
                        onClose={onCloseWindow}
                        onMinimize={onMinimize}
                        canShowMessagesForAllLocations={
                            canShowMessagesForAllLocations
                        }
                        showMessagesForAllLocations={
                            showMessagesForAllLocations
                        }
                        setShowMessagesForAllLocations={
                            setShowMessagesForAllLocations
                        }
                    />
                    <FetchMoreWrapper
                        flex={1}
                        overflowY="auto"
                        position="relative"
                        overflowWrap="anywhere"
                        flexDir="column-reverse"
                        pb={2}
                        css={scrollbarCSS}
                        hasMore={hasMore}
                        fetchMore={handleFetchMore}
                        networkStatus={networkStatus}
                    >
                        {showMessagesSpinner ? (
                            <Center h="100%">
                                <Spinner size="xl" color="brand.500" />
                            </Center>
                        ) : (
                            <MessagesList
                                messages={[...messages].reverse()}
                                chat={chat}
                                refetchMessages={
                                    shouldShowMessagesForAllLocations
                                        ? fetchAllLocationMessages
                                        : refetchChatMessages
                                }
                                showLocationTags={
                                    shouldShowMessagesForAllLocations
                                }
                            />
                        )}
                    </FetchMoreWrapper>
                    <ChatViewInput
                        refetch={
                            shouldShowMessagesForAllLocations
                                ? fetchAllLocationMessages
                                : refetchChatMessages
                        }
                        messagesQueryVariables={messagesQueryVariables}
                        useMessageListCache={!shouldShowMessagesForAllLocations}
                        compact={compact}
                        initialDraft={initialDraft}
                        aiPrompt={aiPrompt}
                    />
                </ExportMessagesProvider>
            </Flex>
            {!hideClientInfo && (
                <>
                    <ChatClientInfo
                        chat={chat}
                        isOpen={showClientInfo}
                        onClose={() => setShowClientInfo(false)}
                    />
                    <ChatClientInfoDrawer
                        chat={chat}
                        isOpen={isOpen}
                        onClose={onClose}
                    />
                </>
            )}
            <SendToUpdateAlert
                isOpen={isSendToUpdateAlertOpen}
                phone={pendingClientPhone}
                defaultPhone={defaultPhone}
                clinicCountry={chat.clinic?.address?.country}
                onClose={() => {
                    setPendingClientPhone(undefined)
                    onCloseSendToUpdateAlert()
                }}
                onConfirm={() => {
                    if (pendingClientPhone) {
                        setClientPhone(pendingClientPhone)
                    }
                    setPendingClientPhone(undefined)
                    onCloseSendToUpdateAlert()
                }}
            />
        </>
    )
}
